import { observer } from 'mobx-react-lite';
import { Box, EmptyState, Flex, Grid, Text, VStack } from '@cad-arc';
import { Panel } from '../panels/panel';
import { RESIZE_DISABLED } from '../panels/panel-configuration';
import { SensorGridWorkspacePanel } from './client-only-panel-types';
import {
    fillSensorGrid,
    findNearestSensors,
    formatSensorCoordinates,
    SENSOR_GRID_SIZE,
} from './helpers';
import { SENSOR_GRID_COLUMNS, SENSOR_GRID_ROWS } from './sensor-grid-layout';
import { SensorGridEventsRail } from './sensor-grid-events-rail';
import { SensorGridTile } from './sensor-grid-tile';
import { useSensorsStore } from './use-sensors-store';

/**
 * Sensors near a point the dispatcher dropped with cmd+click on the map, as a 3 x 3 grid with a
 * merged event rail down the right.
 *
 * The nine cameras are the nine nearest by great-circle distance. There is no server-side
 * proximity query for sensors — `sensors/list` returns the whole department — so the ranking is
 * done client-side over the list already held in the store.
 */
const SensorGridPanelBody = observer(function SensorGridPanelBody({
    latitude,
    longitude,
}: {
    latitude: number | undefined;
    longitude: number | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();

    if (latitude === undefined || longitude === undefined) {
        return (
            <VStack align="stretch" padding={2} flexGrow={1} overflow="auto">
                <EmptyState
                    visual="Camera"
                    title="No location"
                    subtitle="This panel needs a point on the map to measure from."
                />
            </VStack>
        );
    }

    const nearestSensors = findNearestSensors(
        sensorsStore.sensors,
        { latitude, longitude },
        SENSOR_GRID_SIZE
    );
    /*
     * DEMO HACK — see `fillSensorGrid`. Our demo departments have two or three cameras and a grid
     * with six holes does not show the feature, so the tiles cycle through whatever we have.
     * Duplicates are display-only: the event rail is keyed off the deduplicated sensor set.
     */
    const tiles = fillSensorGrid(nearestSensors);

    if (nearestSensors.length === 0) {
        return (
            <VStack align="stretch" padding={2} flexGrow={1} overflow="auto">
                <EmptyState
                    visual="Camera"
                    title="No sensors nearby"
                    subtitle="No sensor in this department has usable coordinates."
                />
            </VStack>
        );
    }

    return (
        <Flex backgroundColor="surface.canvas">
            {/* The tiles size themselves, so the grid, and with it the panel, is as tall as they are. */}
            <Box flexGrow={1} minWidth={0} padding={2}>
                <Grid
                    templateColumns={`repeat(${SENSOR_GRID_COLUMNS}, 1fr)`}
                    templateRows={`repeat(${SENSOR_GRID_ROWS}, auto)`}
                    gap={2}
                >
                    {tiles.map((sensor, index) => (
                        // A repeated sensor appears more than once, so the index is part of the
                        // key — `sensorId` alone is not unique across the tiles.
                        <SensorGridTile key={`${sensor.externalId}-${index}`} sensor={sensor} />
                    ))}
                </Grid>
            </Box>
            <SensorGridEventsRail sensors={nearestSensors} />
        </Flex>
    );
});

const SensorGridPanelHeader = observer(function SensorGridPanelHeader({
    latitude,
    longitude,
}: {
    latitude: number | undefined;
    longitude: number | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const nearestSensors =
        latitude === undefined || longitude === undefined
            ? []
            : findNearestSensors(sensorsStore.sensors, { latitude, longitude }, SENSOR_GRID_SIZE);

    return (
        <Flex flexGrow={1} minWidth={0} alignItems="flex-start" gap={2}>
            <VStack align="stretch" spacing={0} minWidth={0}>
                <Text variant="headingXs" noOfLines={1}>
                    {`Sensor Grid · ${nearestSensors.length} sensor${
                        nearestSensors.length === 1 ? '' : 's'
                    } near dropped pin`}
                </Text>
                <Text variant="caption" color="secondary" fontFamily="mono" noOfLines={1}>
                    {/* The clicked coordinate, not a reverse-geocoded address: nothing here
                        resolves a point to a street, and a wrong address is worse than none. */}
                    {`${formatSensorCoordinates(latitude, longitude) ?? '–'} · sorted by distance`}
                </Text>
            </VStack>
        </Flex>
    );
});

export function SensorGridPanel({
    panel,
}: {
    panel: SensorGridWorkspacePanel;
}): React.ReactElement {
    const latitude = panel.getPanelState('latitude');
    const longitude = panel.getPanelState('longitude');

    return (
        // As tall as its tiles, at the fixed width set in panel-configuration.
        <Panel
            panel={panel}
            testId="SENSOR_GRID_PANEL"
            resizeOptions={RESIZE_DISABLED}
            heightFromContent
            header={<SensorGridPanelHeader latitude={latitude} longitude={longitude} />}
            body={<SensorGridPanelBody latitude={latitude} longitude={longitude} />}
        />
    );
}
